import { useEffect, useState } from "react";
import { Loader2, FileText, Send, Calculator, PawPrint, CalendarDays, Users, Mail, User, Percent, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { nextDay, onCheckinChange } from "../../lib/dates";

function todayPlus(d) { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }

const EMPTY = {
  guest_name: "", guest_email: "", guest_phone: "",
  checkin: todayPlus(14), checkout: todayPlus(15),
  adults: 2, children: 0, pets: false,
};

export default function AdminQuote() {
  const [props, setProps] = useState([]);
  const [propId, setPropId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [discount, setDiscount] = useState({ type: "percent", value: 0 });
  const [quote, setQuote] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const selectedProp = props.find((p) => p.id === propId) || null;
  const isHotel = selectedProp?.type === "hotel";

  useEffect(() => { api.get("/admin/apartments").then((r) => setProps(r.data)).catch(() => {}); }, []);
  useEffect(() => { setQuote(null); }, [propId, roomId, form]);

  const payload = () => ({
    apartment_id: propId,
    room_type_id: roomId || null,
    guest_name: form.guest_name,
    guest_email: form.guest_email,
    guest_phone: form.guest_phone,
    checkin: form.checkin,
    checkout: form.checkout,
    adults: form.adults,
    children: form.children,
    pets: form.pets,
    discount_type: discount.type,
    discount_value: Number(discount.value) || 0,
    origin: window.location.origin,
  });

  const validate = () => {
    if (!propId) { toast.error("Seleziona una struttura"); return false; }
    if (isHotel && !roomId) { toast.error("Seleziona una tipologia di camera"); return false; }
    if (!form.checkin || !form.checkout) { toast.error("Inserisci le date"); return false; }
    return true;
  };

  const runPreview = async () => {
    const { data } = await api.post("/admin/quote/preview", payload());
    setQuote(data);
  };

  const generate = async () => {
    if (!validate()) return;
    setGenerating(true); setQuote(null);
    try { await runPreview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  // Auto-recompute the preview when the discount changes (after a quote exists)
  useEffect(() => {
    if (!quote) return;
    const t = setTimeout(() => { runPreview().catch(() => {}); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [discount.type, discount.value]);

  const send = async () => {
    if (!form.guest_email.trim()) { toast.error("Inserisci l'email del destinatario"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/quote/send", payload());
      if (data.email_sent) toast.success(`Preventivo inviato a ${form.guest_email}`);
      else toast.warning("Preventivo generato ma l'invio email non è riuscito (controlla la configurazione Resend)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const maxG = isHotel ? (selectedProp?.room_types?.find((r) => r.id === roomId)?.max_guests || 8) : (selectedProp?.max_guests || 8);

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-4xl text-fir flex items-center gap-2"><FileText className="text-gold" /> Preventivo</h1>
        <p className="text-fir-deep/60">Genera un preventivo personalizzato, verificalo e invialo via email al cliente.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* FORM */}
        <div className="bg-white rounded-2xl p-6 border border-stone">
          <h2 className="font-display text-2xl text-fir mb-4">Dati del preventivo</h2>

          <Label>Struttura</Label>
          <select data-testid="quote-property-select" value={propId}
            onChange={(e) => { setPropId(e.target.value); setRoomId(""); }} className="input-alpine w-full mb-3 mt-1">
            <option value="">Seleziona struttura…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</option>)}
          </select>

          {isHotel && (
            <>
              <Label>Tipologia camera</Label>
              <select data-testid="quote-room-select" value={roomId} onChange={(e) => setRoomId(e.target.value)} className="input-alpine w-full mb-3 mt-1">
                <option value="">Seleziona camera…</option>
                {(selectedProp?.room_types || []).map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
              </select>
            </>
          )}

          <div className="grid grid-cols-1 gap-3">
            <Field icon={<User size={15} />} label="Nome e cognome">
              <input data-testid="quote-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="input-alpine w-full" placeholder="Mario Rossi" />
            </Field>
            <Field icon={<Mail size={15} />} label="Email destinatario">
              <input data-testid="quote-email" type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="input-alpine w-full" placeholder="cliente@email.com" />
            </Field>
            <Field icon={<Phone size={15} />} label="Numero di telefono">
              <input data-testid="quote-phone" type="tel" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="input-alpine w-full" placeholder="+39 333 1234567" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field icon={<CalendarDays size={15} />} label="Arrivo">
              <input data-testid="quote-checkin" type="date" value={form.checkin} min={todayPlus(0)}
                onChange={(e) => onCheckinChange(e.target.value, form.checkout, setForm)} className="input-alpine w-full" />
            </Field>
            <Field icon={<CalendarDays size={15} />} label="Partenza">
              <input data-testid="quote-checkout" type="date" value={form.checkout} min={nextDay(form.checkin)}
                onChange={(e) => setForm({ ...form, checkout: e.target.value })} className="input-alpine w-full" />
            </Field>
            <Field icon={<Users size={15} />} label="Ospiti (adulti)">
              <input data-testid="quote-adults" type="number" min="1" max={maxG} value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} className="input-alpine w-full" />
            </Field>
            <Field icon={<Users size={15} />} label="Bambini <14">
              <input data-testid="quote-children" type="number" min="0" max={maxG} value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} className="input-alpine w-full" />
            </Field>
          </div>

          <label className="flex items-center gap-2 mt-4 cursor-pointer text-fir">
            <input type="checkbox" data-testid="quote-pets" checked={form.pets} onChange={(e) => setForm({ ...form, pets: e.target.checked })} className="h-5 w-5 accent-[#1E3F20]" />
            <PawPrint size={16} className="text-wood" /> Con animali <span className="text-fir-deep/50 text-sm">(+€60 per l'intero soggiorno)</span>
          </label>

          <button data-testid="quote-generate-btn" onClick={generate} disabled={generating} className="gold-btn w-full py-3 rounded-xl mt-6 flex items-center justify-center gap-2">
            {generating ? <Loader2 className="animate-spin" size={18} /> : <Calculator size={18} />} Genera preventivo
          </button>
        </div>

        {/* PREVIEW */}
        <div className="bg-white rounded-2xl p-6 border border-stone">
          <h2 className="font-display text-2xl text-fir mb-4">Anteprima</h2>
          {!quote ? (
            <div className="text-fir-deep/50 text-sm py-10 text-center">Compila i dati e premi "Genera preventivo" per vedere il riepilogo.</div>
          ) : (
            <div data-testid="quote-preview">
              <Row label="Struttura" value={quote.apartment_title} bold />
              <Row label="Cliente" value={quote.guest_name || "—"} />
              <Row label="Check-in → Check-out" value={`${quote.checkin} → ${quote.checkout}`} />
              <Row label="Ospiti" value={`${quote.adults} adulti${quote.children ? `, ${quote.children} bambini <14` : ""}`} />
              <Row label={`Notti${quote.billed_nights !== quote.num_nights ? ` (fatturate ${quote.billed_nights})` : ""}`} value={`€${quote.avg_per_person_night}/persona/notte`} />
              <div className="border-t border-stone my-3" />
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs uppercase tracking-wide text-fir-deep/60 flex items-center gap-1"><Percent size={13} /> Sconto</span>
                <input data-testid="quote-discount-value" type="number" min="0" value={discount.value}
                  onChange={(e) => setDiscount({ ...discount, value: Number(e.target.value) })} className="input-alpine w-24" />
                <select data-testid="quote-discount-type" value={discount.type}
                  onChange={(e) => setDiscount({ ...discount, type: e.target.value })} className="input-alpine w-20">
                  <option value="percent">%</option>
                  <option value="amount">€</option>
                </select>
              </div>
              <Row label="Totale soggiorno" value={`€${quote.total}`} />
              {quote.pets && <Row label="Supplemento animali" value={`€${quote.pet_fee}`} />}
              {quote.discount_amount > 0 && (
                <div className="flex justify-between text-sm py-1" data-testid="quote-discount-row">
                  <span className="text-green-700">{quote.discount_type === "percent" ? `Sconto (-${quote.discount_value}%)` : "Sconto"}</span>
                  <span className="text-green-700 font-semibold">-€{quote.discount_amount}</span>
                </div>
              )}
              <div className="flex justify-between items-end mt-2 pt-2 border-t border-stone">
                <span className="font-display text-lg text-fir">Totale preventivo</span>
                <span data-testid="quote-total" className="font-display text-3xl text-fir">€{quote.grand_total}</span>
              </div>
              <div className="mt-3 rounded-xl bg-fir/5 p-3 text-sm space-y-1">
                <div className="flex justify-between text-fir-deep"><span>Cauzione alla prenotazione (20%)</span><span className="font-bold text-fir">€{quote.deposit}</span></div>
                <div className="flex justify-between text-fir-deep/70"><span>Saldo all'arrivo</span><span>€{quote.balance_due}</span></div>
              </div>
              <button data-testid="quote-send-btn" onClick={send} disabled={sending} className="w-full py-3 rounded-xl mt-5 flex items-center justify-center gap-2 bg-fir text-ivory hover:bg-fir-deep transition-colors disabled:opacity-50">
                {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Invia preventivo via email
              </button>
              {!form.guest_email && <p className="text-xs text-center text-amber-600 mt-2">Inserisci l'email del destinatario per inviare.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => <label className="text-xs uppercase tracking-wide text-fir-deep/60">{children}</label>;
function Field({ label, icon, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-wide text-fir-deep/60 flex items-center gap-1.5 mb-1">{icon}{label}</span>{children}</label>;
}
function Row({ label, value, bold }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-fir-deep/60">{label}</span><span className={bold ? "font-semibold text-fir" : "text-fir-deep/90"}>{value}</span></div>;
}
